import { FIELD_NAMES, FILTER_TYPES } from "services/search/const";
import { APP_FILTER_TYPES } from "constants/filter";
import { deepMerge } from "utils/object";

// Settings configured here will be merged into the final config object.

let config = {
  /**
   * @param {String} baseurl of the search site location normally set by consuming cms. e.g. http://mysite/search
   */
  baseurl: "./",

  /**
   * @param {Boolean} enableUrlQuery indicates if GET-Params in Url will be set and recognized
   */
  enableUrlQuery: true,

  /**
   * @param {Boolean} skipInitalSearch blocks the initial search request, this is usefull, when only using
   * widgets that have no state based on the searchResult, e.g. you only have the Searchbar on your site.
   */
  skipInitalSearch: false,

  /**
   * @param {Boolean} removeInitialValue removeInitialValue removes the initial search input value, this is
   * useful when e.g. you only have the Searchbar on your site and trigger a search with a zoovu keyword.
   */
  removeInitialValue: false,

  /**
   * @param {Boolean} allowEmptySearch allowEmptySearch allows triggering a search with an empty search input, this is
   * useful when e.g. you want to allow an actual reset of the search.
   */
  allowEmptySearch: false,

  /**
   * @param {Boolean} useSemanticProductType is used to switch between the legacy product type (product_type_name) and new
   * semantic product type (product_type_semantic) filter facet
   */
  useSemanticProductType: false,

  /**
   * @param {Object} tracking Econda-Adapter tracking Object
   */
  tracking: global.tracking || null,

  keywordTriggeredBanner: {
    /**
     * @param {String} baseUrl base url for keyword triggered banners
     */
    baseUrl: "/",

    /**
     * @param {String} queryParameter parameter added to base Url
     */
    queryParameter: "__ktb",

    /**
     * @param {Number} timeout Time until results are rendered without banner being laoded
     */
    timeout: 200,
  },

  suggestions: {
    /**
     * @param {String} baseUrl base url for suggestions
     */
    baseUrl: "/suggestions",

    /**
     * @param {String} queryParameter parameter added to base Url
     */
    queryParameter: "text",

    /**
     * @param {Number} timeout Time until results are rendered without banner being laoded
     */
    timeout: 2000,
  },

  webcodes: {
    /**
     * @param {String} baseUrl base url for webcode backend
     */
    baseUrl: "/",

    /**
     * @param {Boolean} enabled turns webcode search on/off
     */
    enabled: true,

    /**
     * @param {String} path relative path to webcode search
     */
    path: "codes/webcode-ws/api/v1/searchWebCode/",

    /**
     * @param {String} queryParameter parameter added to base Url
     */
    queryParameter: "searchterm",

    /**
     * @param {Number} timeout Time until results are rendered without response
     */
    timeout: 200,
  },

  /**
   * @param {Object} backend configuration object for the search backend (sal layer 2)
   */
  backend: {
    /**
     * @param {string} type - search adapter type: "zoovu" (default) or "client"
     */
    type: "zoovu",

    /**
     * @param {Integer} timeout in miliseconds
     */
    timeout: 5000,

    /**
     * @param {string} zoovuProjectId - zoovu projectId used in zoovu backend adapter
     */
    zoovuProjectId: undefined,

    /**
     * @param {Object} client - configuration for the client-side search adapter
     * @param {string} client.itemSelector - CSS selector for filterable DOM items
     * @param {string} client.dataScriptId - id of the JSON script tag with search data
     */
    client: {
      itemSelector: "",
      dataScriptId: "",
    },
  },

  /**
   * @param {Object} filters definition of available filters and mapping to backend fields
   * @see e.g. http://sal.development.webkatalog.cornelsen.de/v2/product "Durchsuchbare Felder" for
   * possible values
   */
  filters: {
    /**
     * @param {Object} [q] the application name of the available filter with definition object
     */
    query: {
      /**
       * @param {Enum} type of the filter @see /services/search/const.js
       */
      type: FILTER_TYPES.QUERY,

      /**
       * @param {Enum} field solr field name in the corresponding sal-backend layer 2
       */
      field: FIELD_NAMES.KEYWORD,
    },
    band: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.VOLUME_TITLE,
    },
    bildungsbereich: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.EDUCATIONAL_AREAS,
    },
    bundesland: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.FEDERAL_STATES,
    },
    schulform: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.SCHOOL_TYPES,
    },
    fach: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.SCHOOL_SUBJECTS,
    },
    klassenstufe: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.CLASS_LEVELS,
    },
    reihe: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.SERIES_TITLE,
    },
    lizenzform: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.LICENSE_FORM,
    },
    "lizenzform-kurz": {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.LICENSE_FORM_SHORT,
    },
    produktlinie: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.PRODUCT_LINE,
    },
    "relevant-fuer-reihe-band": {
      type: FILTER_TYPES.FACET_PATH,
      field: FIELD_NAMES.PATH_SV,
    },
    "relevant-fuer-reihe-reihenausgabe-band": {
      type: FILTER_TYPES.FACET_PATH,
      field: FIELD_NAMES.PATH_SSV,
    },
    "bildungsbereich-schulform": {
      type: FILTER_TYPES.FACET_PATH,
      field: FIELD_NAMES.PATH_ES,
    },
    zielgruppe: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.TARGET_GROUP_NAME,
    },
    digital: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.DISTRIBUTIONSFORM,
    },
    format: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.FORMAT,
    },
    area: {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.AREA,
    },
    "ger-niveau": {
      type: FILTER_TYPES.FACET,
      field: FIELD_NAMES.GER_NIVEAU,
    },
  },

  /**
   * @param {Object} state the initial application state
   **/
  state: {
    search: {
      loading: true,
      error: false,
    },
    result: {
      offset: 0,
      itemsPerPage: 30,
    },
    keywordTriggeredBanner: {
      markup: "",
    },
    filters: {
      query: {
        filterType: APP_FILTER_TYPES.QUERY,
        label: "Bitte Suchen",
        values: [""],
      },
      band: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Band",
        values: [],
      },
      bundesland: {
        filterType: APP_FILTER_TYPES.SINGLE,
        label: "Bundesland",
        values: [],
      },
      bildungsbereich: {
        filterType: APP_FILTER_TYPES.SINGLE,
        label: "Bildungbereich",
        values: [],
      },
      schulform: {
        filterType: APP_FILTER_TYPES.SINGLE,
        label: "Schulform",
        values: [],
      },
      fach: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Fach",
        values: [],
      },
      klassenstufe: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Klassenstufe",
        values: [],
      },
      produktart: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Produktart",
        values: [],
      },
      "product-type-semantic": {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Produktart",
        values: [],
      },
      reihe: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Lehrwerk-Reihe",
        values: [],
      },
      lizenzform: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Lizenzform",
        values: [],
      },
      "lizenzform-kurz": {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Lizenzform(kurz)",
        values: [],
      },
      produktlinie: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Produktlinie",
        values: [],
      },
      "relevant-fuer-reihe-band": {
        filterType: APP_FILTER_TYPES.CASCADE,
        label: "Reihe/Band",
        columnCount: 2,
        values: [],
      },
      "relevant-fuer-reihe-reihenausgabe-band": {
        filterType: APP_FILTER_TYPES.CASCADE,
        label: "Lehrwerk/Reihe",
        columnCount: 3,
        values: [],
      },
      "bildungsbereich-schulform": {
        filterType: APP_FILTER_TYPES.CASCADE,
        label: "Bereich/Schulform",
        columnCount: 2,
        values: [],
      },
      zielgruppe: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Zielgruppe",
        values: [],
      },
      digital: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Digital",
        values: [],
      },
      format: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Format",
        values: [],
      },
      area: {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "Ort",
        values: [],
      },
      "ger-niveau": {
        filterType: APP_FILTER_TYPES.MULTIPLE,
        label: "GER-Niveau",
        values: [],
      },
    },
    suggestions: {
      terms: [],
      limit: 10,
    },
    sortOptions: [],
    // This option is only relevant for zoovu and is ignored, when using the solr baackend
    rankingStrategy: {
      id: "",
    },
    webcode: {
      loading: false,
    },
  },

  /**
   * @param {Object} redirects configuration object for redirects coming from the search backend
   */
  redirects: {
    // eslint-disable-next-line no-useless-escape
    immediate: ["^(?:https?\:\/\/)?www\.cornelsen\.de"],

    external: [
      // eslint-disable-next-line no-useless-escape
      "^(?:https?\:\/\/)?www\.cornelsen\.ch",
      // eslint-disable-next-line no-useless-escape
      "^(?:https?\:\/\/)?cornelsen-ja-zur-mail\.de",
      // eslint-disable-next-line no-useless-escape
      "^(?:https?\:\/\/)?(?:www|info)\.bildungslogin\.de",
      // eslint-disable-next-line no-useless-escape
      "^(?:https?\:\/\/)?(?:mein|akademie|veranstaltungen|lernen|lesen)\.cornelsen\.de",
      // eslint-disable-next-line no-useless-escape
      "^(?:https?\:\/\/)?portal.cornelsen.ai",
    ],
  },
};

const configurationJson =
  document.getElementById("sal-config") &&
  document.getElementById("sal-config").innerHTML;

if (configurationJson && configurationJson.trim().length) {
  try {
    const extendingConfig = JSON.parse(configurationJson);

    config = deepMerge({}, config, extendingConfig);
  } catch {
    console.error("The configuration is not valid JSON");
  }
}

/**
 * Resolves which product type filter key/definition should be registered on
 * config.filters, depending on config.useSemanticProductType.
 */
export function getProductTypeFilter(useSemanticProductType) {
  return useSemanticProductType
    ? {
        key: "product-type-semantic",
        filter: {
          type: FILTER_TYPES.FACET,
          field: FIELD_NAMES.PRODUCT_TYPE_SEMANTIC,
        },
      }
    : {
        key: "produktart",
        filter: {
          type: FILTER_TYPES.FACET,
          field: FIELD_NAMES.PRODUCTTYPES,
        },
      };
}

const productTypeFilter = getProductTypeFilter(config.useSemanticProductType);

config.filters[productTypeFilter.key] = productTypeFilter.filter;

export default config;
